import logo from 'assets/header-prevencor-xx.png';
import AssinaturaEletronica from 'components/core/AssinaturaEletronica';
import LaudoActionButtons from 'components/laudos/LaudoActionButtons';
import { Card, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { selectEcoById } from 'services/ecodoppler';
import useSWR from 'swr';
import { generatePDFEco } from './generatePDFEco';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function getFirstText(item, keys = ['DESCRICAO']) {
  for (const key of keys) {
    const value = String(item?.[key] ?? '').trim();
    if (value) return value;
  }
  return '';
}

export default function ReportEcodoppler() {
  const { id } = useParams();
  const {
    data: eco,
    isLoading,
    error,
  } = useSWR(id ? ['ecodoppler', id] : null, () => selectEcoById(id));

  const codeEco = eco?.CODECO || 'eco';

  const renderList = (items, keyPrefix, keys = ['DESCRICAO']) =>
    asArray(items).map((item, index) => {
      const text = getFirstText(item, keys);
      if (!text) return null;

      return (
        <div key={`${keyPrefix}-${codeEco}-${text.substring(0, 20)}-${index}`} className="row">
          <div className="col-12"> - {text}</div>
        </div>
      );
    });

  const showSection = (items) => asArray(items).length > 0;

  const hasDopplerMitral =
    eco?.AVM != null || eco?.ORIFICIO_VM != null || eco?.GMVM != null || eco?.VOLUME_VM != null;

  const hasDopplerAortico = eco?.AREA_AO != null || eco?.GMVAO != null || eco?.GMVSVE != null;

  const hasDopplerTricuspide = eco?.GMVT != null || eco?.estimativaPSVD != null;

  const hasDopplerPulmonar = eco?.GMVP != null;

  const hasProteseMitral = eco?.GMPM != null || showSection(eco?.refluxoPM);
  const hasProteseAortica = eco?.GMPAO != null || showSection(eco?.refluxoPAO);
  const hasProteseTricuspide = eco?.GMPT != null || showSection(eco?.refluxoPT);
  const hasProtesePulmonar = eco?.GMPP != null || showSection(eco?.refluxoPP);
  const ivaeVolumeAe = eco?.VOL_AE ?? eco?.VTIVSVE4;
  const feSimpson = eco?.FRAEJECBIPLA ?? eco?.FE;

  const veLeftColumn = [
    eco?.SEPTO != null ? `Septo: ${Number(eco.SEPTO).toFixed(1)} (cm)` : null,
    eco?.PPVE != null ? `PP VE: ${Number(eco.PPVE).toFixed(1)} (cm)` : null,
    eco?.SEPTO != null && eco?.PPVE != null && Number(eco.PPVE) !== 0
      ? `Relação Septo/PP VE: ${(Number(eco.SEPTO) / Number(eco.PPVE)).toFixed(2)}`
      : null,
    eco?.DD != null ? `Diâmetro Diastólico: ${Number(eco.DD).toFixed(1)} (cm)` : null,
    eco?.DS != null ? `Diâmetro Sistólico: ${Number(eco.DS).toFixed(1)} (cm)` : null,
    eco?.DD != null && eco?.DS != null && Number(eco.DD) !== 0
      ? `Fração de Encurtamento (%): ${(((Number(eco.DD) - Number(eco.DS)) / Number(eco.DD)) * 100).toFixed(1)}`
      : null,
  ].filter(Boolean);

  const veRightColumn = [
    eco?.fracaoEjecaoMono != null ? `Fração de Ejeção(Monoplanar): ${eco.fracaoEjecaoMono}` : null,
    eco?.massaVE != null ? `Massa do VE (g/m2): ${eco.massaVE}` : null,
    feSimpson != null
      ? `Cálculo da FE (Método de Simpson)-(%): ${Number(feSimpson).toFixed(0)}`
      : null,
  ].filter(Boolean);

  return (
    <>
      <Card className="mt-2 mb-2">
        <Card.Header className="text-center font-weight-bold">
          <img
            src={logo}
            alt="Prevencor"
            style={{ display: 'block', maxWidth: '100%', margin: '0 auto 8px' }}
            className="no-print"
          />
          <div>LAUDO DE ECOCARDIOGRAMA</div>
          <u>Eco Uni e Bi com Doppler Color</u>
        </Card.Header>

        {isLoading && (
          <Card.Body>
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          </Card.Body>
        )}

        {error && !isLoading && (
          <Card.Body className="text-center text-danger py-5">
            {error.message || 'Erro ao carregar o laudo.'}
          </Card.Body>
        )}

        {eco && !isLoading && !error && (
          <>
            <Card.Body>
              <strong className="mr-auto">Nome do Paciente: </strong>
              {`${eco.CODPAC} - ${eco.NOMEPAC}`}
              <br />
              <strong className="mr-auto">Data de Nasc: </strong>
              {`${eco.dataNascFmt}`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Sexo: </strong>
              {`${eco.Sexo}`}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Idade: </strong>
              {`${eco.idadePAC} anos`}
              <br />
              <strong className="mr-auto">Convênio: </strong>
              {eco.SIGLACONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Cod Eco: </strong>
              {eco.CODECO}&nbsp;&nbsp;&nbsp;
              <strong className="mr-auto">Data do Exame: </strong>
              {eco.dataEcoFmt}
              <br />
              <strong className="mr-auto">Médico que Realizou: Dr. </strong>
              {`${eco.Realizou}`}
              <br />
              <strong className="mr-auto">Médico que Solicitou: Dr. </strong>
              {`${eco.Solicitou}`}
              <hr />
              {showSection(eco.obs) && (
                <>
                  <strong className="mr-auto">Observações Gerais: </strong>
                  {renderList(eco.obs, 'obs')}
                  <br />
                </>
              )}
              <strong className="mr-auto">
                Diâmetros e Função Sistólica do Ventrículo Esquerdo{' '}
              </strong>
              {Array.from({
                length: Math.max(veLeftColumn.length, veRightColumn.length),
              }).map((_, index) => (
                <div className="row" key={`ve-col-${index}`}>
                  <div className="col-6">{veLeftColumn[index] || ''}</div>
                  <div className="col-6">{veRightColumn[index] || ''}</div>
                </div>
              ))}
              <br></br>
              <u className="mr-auto">Achados Descritivos do VE: </u>
              {renderList(eco.achados, 'achados')}
              <br />
              {showSection(eco.contraRegiao) && (
                <>
                  <u className="mr-auto">Contração Segmentar: </u>
                  {renderList(eco.contraRegiao, 'contraRegiao', ['CONTRASEG'])}
                  <br />
                </>
              )}
              <strong className="mr-auto">
                Avaliação da Função Diastólica do Ventrículo Esquerdo{' '}
              </strong>
              {eco.indicePM != null && !Number.isNaN(Number(eco.indicePM)) && (
                <div className="row">
                  <div className="col-12">{`Índice de Performance: ${Number(eco.indicePM).toFixed(2)} (Normal < 0.42)`}</div>
                </div>
              )}
              {eco.estimativaDC != null && !Number.isNaN(Number(eco.estimativaDC)) && (
                <div className="row">
                  <div className="col-12">{`Estimativa do Débito Cardíaco: ${eco.estimativaDC} (l/min/m2) (2.4 a 4.2)`}</div>
                </div>
              )}
              {renderList(eco.avDiast, 'avDiast')}
              {showSection(eco.avDiast) && <br />}
              <strong className="mr-auto">Átrio Esquerdo </strong>
              {eco.DAE != null ? (
                <div className="row">
                  <div className="col-12">{`Diâmetro do Átrio Esquerdo: ${eco.DAE?.toFixed(1)} (cm)`}</div>
                </div>
              ) : null}
              {ivaeVolumeAe != null ? (
                <div className="row">
                  <div className="col-12">{`IVAE (Volume AE): ${Number(ivaeVolumeAe).toFixed(0)} (Normal até 34 ml/m2)`}</div>
                </div>
              ) : null}
              {renderList(eco.atrioEsq, 'atrioEsq')}
              {showSection(eco.atrioEsq) && <br />}
              {showSection(eco.atrioDir) && (
                <>
                  <strong className="mr-auto">Átrio Direito </strong>
                  {renderList(eco.atrioDir, 'atrioDir')}
                  <br />
                </>
              )}
              {showSection(eco.veiaCavaInf) && (
                <>
                  <strong className="mr-auto">Veia Cava Inferior </strong>
                  {renderList(eco.veiaCavaInf, 'veiaCavaInf')}
                  <br />
                </>
              )}
              {(eco?.VA != null || showSection(eco.ventriDir)) && (
                <>
                  <strong className="mr-auto">Ventrículo Direito </strong>
                  {eco?.VA != null ? (
                    <div className="row">
                      <div className="col-12">{`Diâmetro Ventrículo Direito: ${Number(eco.VA).toFixed(1)} (cm)`}</div>
                    </div>
                  ) : null}
                  {renderList(eco.ventriDir, 'ventriDir')}
                  <br />
                </>
              )}
              {(eco?.VTIVSVE1 != null || showSection(eco.aorta)) && (
                <>
                  <strong className="mr-auto">Aorta </strong>
                  {eco?.VTIVSVE1 != null ? (
                    <div className="row">
                      <div className="col-12">{`Diâmetro Aorta: ${Number(eco.VTIVSVE1).toFixed(1)} (cm)`}</div>
                    </div>
                  ) : null}
                  {renderList(eco.aorta, 'aorta')}
                  <br />
                </>
              )}
              {(showSection(eco.valvaMitral) || showSection(eco.refVM)) && (
                <>
                  <strong className="mr-auto">Valva Mitral </strong>
                  {renderList(eco.valvaMitral, 'valvaMitral')}
                  {renderList(eco.refVM, 'refVM')}
                  <br />
                </>
              )}
              {(eco.DAVAO != null || showSection(eco.valvaAO) || showSection(eco.refVAO)) && (
                <strong className="mr-auto">Valva Aórtica </strong>
              )}
              {eco.DAVAO != null ? (
                <div className="row">
                  <div className="col-12">{`Diâmetro da Raiz Aórtica: ${eco.DAVAO?.toFixed(1)} (cm)`}</div>
                </div>
              ) : null}
              {(showSection(eco.valvaAO) || showSection(eco.refVAO)) && (
                <>
                  {renderList(eco.valvaAO, 'valvaAO')}
                  {renderList(eco.refVAO, 'refVAO')}
                  <br />
                </>
              )}
              {(showSection(eco.valvaTricu) || showSection(eco.refVT)) && (
                <>
                  <strong className="mr-auto">Valva Tricúspide</strong>
                  {renderList(eco.valvaTricu, 'valvaTricu')}
                  {renderList(eco.refVT, 'refVT')}
                  <br />
                </>
              )}
              {(eco.DAVP != null || showSection(eco.valvaPulmo) || showSection(eco.refVP)) && (
                <strong className="mr-auto">Valva Pulmonar</strong>
              )}
              {eco.DAVP != null ? (
                <div className="row">
                  <div className="col-12">{`Diâmetro do Anel Pulmonar: ${eco.DAVP.toFixed(1)} (cm)`}</div>
                </div>
              ) : null}
              {(showSection(eco.valvaPulmo) || showSection(eco.refVP)) && (
                <>
                  {renderList(eco.valvaPulmo, 'valvaPulmo')}
                  {renderList(eco.refVP, 'refVP')}
                  <br />
                </>
              )}
              {showSection(eco.pericardio) && (
                <>
                  <strong className="mr-auto">Pericárdio</strong>
                  {renderList(eco.pericardio, 'pericardio')}
                  <br />
                </>
              )}
              {hasDopplerMitral && <strong className="mr-auto">Doppler Mitral</strong>}
              <div className="row">
                {eco.AVM != null ? (
                  <div className="col-12">{`Área Valva Mitral: ${eco.AVM.toFixed(1)} (cm2) ${eco.valorAVM || ''}`}</div>
                ) : null}
                {eco.ORIFICIO_VM != null ? (
                  <div className="col-12">{`Orifício Regurgitação Mitral (PISA): ${eco.ORIFICIO_VM.toFixed(2)} (cm2)`}</div>
                ) : null}
              </div>
              <div className="row">
                {eco.GMVM != null ? (
                  <div className="col-12">{`Gradiente Médio: ${eco.GMVM} (mmHg)`}</div>
                ) : null}
                {eco.VOLUME_VM != null ? (
                  <div className="col-12">{`Volume Regurgitação Mitral: ${eco.VOLUME_VM} (cc)`}</div>
                ) : null}
              </div>
              {hasDopplerAortico && <strong className="mr-auto">Doppler Aórtico</strong>}
              {eco.AREA_AO != null ? (
                <div className="row">
                  <div className="col-12">{`Área Valva Aórtica: ${eco.AREA_AO.toFixed(2)} (cm2) ${eco.valorAVAO || ''}`}</div>
                </div>
              ) : null}
              {eco.GMVAO != null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Valva Aórtica: ${eco.GMVAO} (mmHg)`}</div>
                </div>
              ) : null}
              {eco.GMVSVE != null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio Via de Saída do VE: ${eco.GMVSVE} (mmHg)`}</div>
                </div>
              ) : null}
              {hasDopplerTricuspide && <strong className="mr-auto">Doppler Tricúspide</strong>}
              {eco.GMVT != null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Valva Tricúspide: ${eco.GMVT} (mmHg)`}</div>
                </div>
              ) : null}
              {eco.estimativaPSVD != null ? (
                <div className="row">
                  <div className="col-12">{`Estimativa da Pressão Sistólica da AP: ${eco.estimativaPSVD} (mmHg)`}</div>
                </div>
              ) : null}
              {hasDopplerPulmonar && <strong className="mr-auto">Doppler Pulmonar</strong>}
              {eco.GMVP != null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Valva Pulmonar: ${eco.GMVP} (mmHg)`}</div>
                </div>
              ) : null}
              {hasProteseMitral && <strong className="mr-auto">Prótese Mitral</strong>}
              {eco.GMPM != null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Prótese Mitral: ${eco.GMPM} (mmHg)`}</div>
                </div>
              ) : null}
              {showSection(eco.refluxoPM) && (
                <>
                  {renderList(eco.refluxoPM, 'refluxoPM', ['refluxoPM'])}
                  <br />
                </>
              )}
              {hasProteseAortica && <strong className="mr-auto">Prótese Aórtica</strong>}
              {eco.GMPAO != null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Prótese Aórtica: ${eco.GMPAO} (mmHg)`}</div>
                </div>
              ) : null}
              {showSection(eco.refluxoPAO) && (
                <>
                  {renderList(eco.refluxoPAO, 'refluxoPAO', ['refluxoPAO'])}
                  <br />
                </>
              )}
              {hasProteseTricuspide && <strong className="mr-auto">Prótese Tricúspide</strong>}
              {eco.GMPT != null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Prótese Tricúspide: ${eco.GMPT} (mmHg)`}</div>
                </div>
              ) : null}
              {showSection(eco.refluxoPT) && (
                <>
                  {renderList(eco.refluxoPT, 'refluxoPT', ['refluxoPT'])}
                  <br />
                </>
              )}
              {hasProtesePulmonar && <strong className="mr-auto">Prótese Pulmonar</strong>}
              {eco.GMPP != null ? (
                <div className="row">
                  <div className="col-12">{`Gradiente Médio da Prótese Pulmonar: ${eco.GMPP} (mmHg)`}</div>
                </div>
              ) : null}
              {showSection(eco.refluxoPP) && (
                <>
                  {renderList(eco.refluxoPP, 'refluxoPP', ['refluxoPP'])}
                  <br />
                </>
              )}
              {String(eco.OBSERVACOES || '').trim() && (
                <>
                  <strong className="mr-auto">Observações Finais</strong>
                  <div className="row">
                    <div className="col-12">{`${eco.OBSERVACOES}`}</div>
                  </div>
                  <br />
                </>
              )}
              <strong className="mr-auto">Conclusões</strong>
              {renderList(eco.valvulopatia, 'valvulopatia', ['valvulopatia'])}
              {renderList(eco.protese, 'protese', ['protese'])}
              {renderList(eco.contraSeg, 'contraSeg', ['contraSeg'])}
              {renderList(eco.conclusoes, 'conclusoes', ['conclusoes'])}
            </Card.Body>

            <Card.Footer className="text-center no-print">
              <LaudoActionButtons
                actions={[
                  {
                    key: 'timbrado',
                    label: 'Timbrado',
                    onClick: () => generatePDFEco({ eco }),
                  },
                ]}
              />
            </Card.Footer>
            <Card.Footer className="text-center">
              <AssinaturaEletronica data={eco} nome={eco.Realizou} showPrintButton={false} />
            </Card.Footer>
          </>
        )}
      </Card>
    </>
  );
}
